import React, { useState, useEffect } from "react";
import { AlertTriangle, ShieldCheck, Lock, Loader } from "lucide-react";
import {
  fetchDisclosureAck,
  writeDisclosureAck,
  getCurrentUid,
} from "../../services/disclosureService";

/**
 * ResearchDisclosureGate
 *
 * Blocks the entire Research tab until the user reads each of the five
 * sections below and checks the box for each one. Acknowledgment is
 * persisted to Firestore so it survives device changes, cache wipes,
 * and incognito mode.
 *
 * Five sections — note Module B (13F holdings) was dropped, so the 13F
 * lag section from the original PRD is not included here.
 *
 * To force every user to re-acknowledge after a material copy change,
 * bump `DISCLOSURE_VERSIONS.research` in disclosureService.js.
 */

const KIND = "research";

const SECTIONS = [
  {
    id: "intro",
    title: "Educational research only — not investment advice",
    body:
      "The Research tab is an educational tool. It surfaces third-party " +
      "analyst stances and our own objective risk-character bucket for " +
      "selected stocks. ARCrypto is not a registered investment adviser. " +
      "Nothing in this section constitutes a recommendation to buy, sell, " +
      "or hold any specific asset.",
  },
  {
    id: "notpersonalized",
    title: "Not personalized",
    body:
      "What you see here is the same for every student in your risk-profile " +
      "bracket. It does not account for your full financial picture — your " +
      "taxes, debts, dependents, employment, insurance, real estate, estate " +
      "planning, or your local regulatory environment.",
  },
  {
    id: "thirdparty",
    title: "Analyst data is third-party and lagged",
    body:
      "Buy / Hold / Sell stances come from named third-party analysts and " +
      "their firms, displayed exactly as published. ARCrypto does not " +
      "endorse those stances. Analyst data is provided by an external " +
      "vendor and may be hours or days behind the live market.",
  },
  {
    id: "past",
    title: "Past performance and 'average return' are not predictive",
    body:
      "Analyst success rate and average return figures shown on the cards " +
      "are historical statistics from third-party vendors and do not " +
      "predict future outcomes. Methodologies, sample windows, and survivor " +
      "biases vary by source.",
  },
  {
    id: "yours",
    title: "Risk of total loss — your decisions are yours",
    body:
      "Investing in individual stocks can result in significant losses, " +
      "including total loss of capital. Leveraged and high-volatility " +
      "names are especially exposed. You alone are responsible for any " +
      "decisions you make based on what you see here. Consult a licensed " +
      "investment advisor, tax professional, or attorney before acting.",
  },
];

const ResearchDisclosureGate = ({ onAcknowledge }) => {
  const [checked, setChecked] = useState({});
  const [loadingAck, setLoadingAck] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // On mount: try to load an existing acknowledgment from Firestore.
  // If one exists for the current version, skip the gate entirely.
  useEffect(() => {
    let cancelled = false;
    const uid = getCurrentUid();
    if (!uid) {
      setLoadingAck(false);
      return;
    }
    fetchDisclosureAck(uid, KIND)
      .then((record) => {
        if (cancelled) return;
        if (record?.acknowledgedAt) {
          onAcknowledge?.(record);
        }
        setLoadingAck(false);
      })
      .catch(() => {
        if (!cancelled) setLoadingAck(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allChecked = SECTIONS.every((s) => checked[s.id]);

  const handleCheck = (id) => (e) => {
    setChecked((curr) => ({ ...curr, [id]: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (!allChecked || submitting) return;
    const uid = getCurrentUid();
    if (!uid) {
      setError("You need to be signed in to acknowledge. Please refresh and try again.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const record = await writeDisclosureAck(
        uid,
        KIND,
        SECTIONS.map((s) => s.id)
      );
      onAcknowledge?.(record);
    } catch (err) {
      setError(err.message || "Could not save your acknowledgment. Please try again.");
      setSubmitting(false);
    }
  };

  if (loadingAck) {
    return (
      <div className="surface-xl p-10 flex items-center justify-center gap-3 text-arc-muted">
        <Loader className="animate-spin" size={18} />
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 border-2 border-amber-500/30 mb-4">
          <Lock size={28} className="text-amber-400" />
        </div>
        <p className="eyebrow text-amber-400 mb-2">
          BEFORE YOU SEE THE RESEARCH  ·  REQUIRED
        </p>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-arc-ink mb-3">
          Read &amp; acknowledge the disclosure
        </h1>
        <p className="text-arc-muted max-w-2xl mx-auto leading-relaxed text-sm sm:text-base">
          Please read each of the five sections below and check the box next to
          each one to confirm you understand it. The Research tab will unlock
          once all five are checked.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {SECTIONS.map((s, idx) => {
          const isChecked = !!checked[s.id];
          return (
            <label
              key={s.id}
              className={`block rounded-2xl border-2 p-4 sm:p-5 cursor-pointer transition-all ${
                isChecked
                  ? "border-emerald-500/50 bg-emerald-500/5"
                  : "border-amber-500/30 bg-amber-500/5 hover:border-amber-500/50"
              }`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={handleCheck(s.id)}
                  className="mt-1 w-5 h-5 flex-shrink-0 accent-arc-gold cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <span
                      className={`text-xs font-mono font-bold ${
                        isChecked ? "text-emerald-400" : "text-amber-400"
                      }`}
                    >
                      {idx + 1}/{SECTIONS.length}
                    </span>
                    <h3 className="font-bold text-sm sm:text-base text-arc-ink">
                      {s.title}
                    </h3>
                  </div>
                  <p className="text-xs sm:text-sm text-arc-muted leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Submit */}
      <div className="rounded-2xl border border-arc-line bg-arc-surface p-5">
        <div className="flex items-center gap-3 mb-4">
          {allChecked ? (
            <>
              <ShieldCheck size={20} className="text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-300">
                All {SECTIONS.length} sections acknowledged — ready to unlock.
              </p>
            </>
          ) : (
            <>
              <AlertTriangle size={20} className="text-amber-400 flex-shrink-0" />
              <p className="text-sm text-arc-muted">
                <span className="font-semibold text-arc-ink">
                  {Object.values(checked).filter(Boolean).length} of{" "}
                  {SECTIONS.length}
                </span>{" "}
                sections checked. Please review and check each section before
                continuing.
              </p>
            </>
          )}
        </div>

        {error && (
          <div className="mb-3 p-3 rounded-lg border border-red-400/40 bg-red-500/10 text-xs sm:text-sm text-red-200">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!allChecked || submitting}
          className={`btn-tactile w-full px-6 py-3.5 rounded-xl text-sm sm:text-base font-semibold transition-all ${
            allChecked && !submitting
              ? "btn-gold cursor-pointer"
              : "bg-arc-line text-arc-dim cursor-not-allowed"
          }`}
        >
          {submitting
            ? "Saving…"
            : allChecked
            ? "I Agree — Unlock Research"
            : `Check All ${SECTIONS.length} Sections to Continue`}
        </button>

        <p className="text-[11px] text-arc-dim mt-3 text-center">
          Your acknowledgment is recorded server-side with today's date. You
          won't be asked again on any device unless the disclosure language is
          updated.
        </p>
      </div>
    </div>
  );
};

export default ResearchDisclosureGate;
